import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { canGoBack } from "./history";
import { isStandalone, PHONE_QUERY } from "./media";

/**
 * Keeps the app the size of what the person can see. When the keyboard opens, the phone shrinks the visible
 * area but not the page, so a bar at the bottom would sit under the keys: the app follows the visible area
 * instead, and `--kb` says how tall the keyboard is for anything fixed to the bottom. A pinch zoom leaves
 * everything alone.
 */
export function usePhoneViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    let frame = 0;
    const apply = () => {
      frame = 0;
      if (Math.abs(vv.scale - 1) > 0.01) return;
      const kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      root.style.setProperty("--kb", `${kb}px`);
      root.style.setProperty("--vvh", `${Math.round(vv.height)}px`);
      root.style.setProperty("--vvt", `${Math.round(vv.offsetTop)}px`);
      root.classList.toggle("kb-open", kb > 80);
    };
    const later = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    apply();
    vv.addEventListener("resize", later);
    vv.addEventListener("scroll", later);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener("resize", later);
      vv.removeEventListener("scroll", later);
      for (const k of ["--kb", "--vvh", "--vvt"]) root.style.removeProperty(k);
      root.classList.remove("kb-open");
    };
  }, []);
}

const EDGE_PX = 24;
const SWIPE_BACK_PX = 80;

/** Set when a swipe back already moved the page (the app's own or the browser's), so that back does not slide it again. */
export const swipe = { back: false };

/**
 * Swipe in from the left edge to go back. Safari already does this in the browser; opened from the home
 * screen there is no browser, so the app does it, moving the page with the finger.
 */
export function useEdgeSwipeBack(): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (!isStandalone()) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let page: HTMLElement | null = null;
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      tracking = e.touches.length === 1 && t.clientX <= EDGE_PX && canGoBack() && window.matchMedia(PHONE_QUERY).matches;
      if (!tracking) return;
      startX = t.clientX;
      startY = t.clientY;
      page = document.querySelector<HTMLElement>(".pn-app > .pn-main");
    };
    const onMove = (e: TouchEvent) => {
      if (!tracking || !page) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      if (Math.abs(t.clientY - startY) > 40 && dx < 30) {
        tracking = false;
        page.style.transform = "";
        return;
      }
      page.style.transition = "none";
      page.style.transform = `translateX(${Math.max(0, dx)}px)`;
    };
    const onEnd = (e: TouchEvent) => {
      if (!tracking || !page) return;
      tracking = false;
      const dx = (e.changedTouches[0]?.clientX ?? startX) - startX;
      const el = page;
      el.style.transition = "transform .22s ease";
      if (dx > SWIPE_BACK_PX) {
        el.style.transform = "translateX(100%)";
        window.setTimeout(() => {
          swipe.back = true;
          navigate(-1);
          el.style.transition = "none";
          el.style.transform = "";
        }, 200);
      } else {
        el.style.transform = "";
      }
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [navigate]);
}

/** How far the finger moves before a pull counts, and how far the circle then has to travel. */
const PULL_DEADZONE_PX = 10;
const PULL_REFRESH_PX = 64;
const PULL_MAX_PX = 96;
/** Where a pull never starts: anything that takes a finger's drag itself, or holds unsaved typing. */
const NO_PULL = "input, textarea, select, [contenteditable='true'], .pn-chips, .pn-sheet-scrim, .scrim.on, [data-no-pull]";

/** True when every scroller from the finger up to the page is at its top. */
function atTop(from: Element, page: HTMLElement): boolean {
  for (let el: Element | null = from; el; el = el.parentElement) {
    if (el instanceof HTMLElement && el.scrollTop > 0) {
      const y = getComputedStyle(el).overflowY;
      if (y === "auto" || y === "scroll") return false;
    }
    if (el === page) return true;
  }
  return true;
}

/**
 * Pull down at the top of a screen to refresh it, as phone apps do. The app scrolls inside itself, so the
 * browser's own pull never fires (and the home screen app has none): the app draws its own circle, and on
 * release asks the screen to load again. Returns the circle's element for the shell to place.
 */
export function usePullToRefresh(onRefresh: () => void): RefObject<HTMLDivElement | null> {
  const ring = useRef<HTMLDivElement>(null);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let state: "idle" | "maybe" | "pulling" = "idle";
    let pull = 0;
    let page: HTMLElement | null = null;
    const show = (y: number, ready: boolean) => {
      const el = ring.current;
      if (!el) return;
      el.style.transform = `translate(-50%, ${y - 48}px) rotate(${Math.round(y * 4)}deg)`;
      el.style.opacity = String(Math.min(1, y / 40));
      el.classList.toggle("ready", ready);
    };
    const settle = (spin: boolean) => {
      const el = ring.current;
      if (!el) return;
      el.classList.add("settling");
      if (spin) {
        el.classList.add("spinning");
        el.style.transform = "translate(-50%, 16px)";
        el.style.opacity = "1";
        window.setTimeout(() => {
          el.classList.remove("spinning", "ready");
          el.style.transform = "translate(-50%, -48px)";
          el.style.opacity = "0";
        }, 750);
      } else {
        el.classList.remove("ready");
        el.style.transform = "translate(-50%, -48px)";
        el.style.opacity = "0";
      }
      window.setTimeout(() => el.classList.remove("settling"), 950);
    };

    const onStart = (e: TouchEvent) => {
      state = "idle";
      if (e.touches.length !== 1 || !window.matchMedia(PHONE_QUERY).matches) return;
      const target = e.target as Element | null;
      page = document.querySelector<HTMLElement>(".pn-app > .pn-main");
      if (!target || !page || !page.contains(target) || target.closest(NO_PULL)) return;
      // A screen with an unsaved change keeps it: a refresh would throw it away.
      if (document.querySelector(".pn-savebar.on")) return;
      if (!atTop(target, page)) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      pull = 0;
      state = "maybe";
    };
    const onMove = (e: TouchEvent) => {
      if (state === "idle") return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (state === "maybe") {
        if (Math.abs(dx) > Math.abs(dy) || dy < -4) {
          state = "idle";
          return;
        }
        if (dy < PULL_DEADZONE_PX) return;
        state = "pulling";
      }
      // The pull follows the finger at half speed, the way a rubber band gives.
      pull = Math.max(0, Math.min(PULL_MAX_PX, (dy - PULL_DEADZONE_PX) * 0.5));
      show(pull, pull >= PULL_REFRESH_PX);
    };
    const onEnd = () => {
      if (state !== "pulling") {
        state = "idle";
        return;
      }
      state = "idle";
      const go = pull >= PULL_REFRESH_PX;
      settle(go);
      if (go) refresh.current();
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return ring;
}

/**
 * A new screen opens at its top; going back returns to where that screen was left, and a tab (`isTab`) opens
 * where it was left, as a phone's tabs do. Why: on a phone and an iPad every screen shares the one `.pn-main`
 * scroller, so without this a screen opened from halfway down a long list opened halfway down too.
 */
export function useScrollMemory(main: RefObject<HTMLElement | null>, isTab: (pathname: string) => boolean = () => false): void {
  const { key, pathname } = useLocation();
  const type = useNavigationType();
  const saved = useRef(new Map<string, number>());
  const byTab = useRef(new Map<string, number>());
  const current = useRef(key);
  // A page that keeps its filters in the address changes the key, not the place.
  const place = useRef(pathname);

  useEffect(() => {
    const el = main.current;
    if (!el) return;
    const remember = () => {
      saved.current.set(current.current, el.scrollTop);
      byTab.current.set(place.current, el.scrollTop);
    };
    el.addEventListener("scroll", remember, { passive: true });
    return () => el.removeEventListener("scroll", remember);
  }, [main]);

  useLayoutEffect(() => {
    current.current = key;
    const samePlace = place.current === pathname;
    place.current = pathname;
    const el = main.current;
    if (!el || samePlace) return;
    el.scrollTop = type === "POP" ? (saved.current.get(key) ?? 0) : isTab(pathname) ? (byTab.current.get(pathname) ?? 0) : 0;
  }, [key, pathname, type, main]);
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredInstall: InstallPromptEvent | null = null;
// Why: Chrome offers installing once, early; holding the offer lets More show one "Add to home screen" row
// instead of a bar over the page.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstall = e as InstallPromptEvent;
  });
}

/** Chrome's own install offer, when it made one. */
export function installPrompt(): InstallPromptEvent | null {
  return deferredInstall;
}

export function onInstalled(): void {
  deferredInstall = null;
}

/**
 * What a phone needs to put the signed-in app on its home screen: the manifest, the icon and the title,
 * plus edge-to-edge drawing so the app keeps clear of the notch itself. Added by the app only, so a page a
 * visitor sees never offers itself as the app. On an iPhone, text boxes never zoom the page.
 */
export function useInstallable(title: string): void {
  useEffect(() => {
    const viewport = document.head.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const before = viewport?.content ?? null;
    if (viewport && before !== null) {
      const parts = [before];
      if (!before.includes("viewport-fit")) parts.push("viewport-fit=cover");
      // Why: an iPhone zooms into any text box under 16px and leaves the page zoomed. This stops only that;
      // iOS still lets a person pinch to zoom.
      if (/iPhone|iPod/.test(navigator.userAgent) && !before.includes("maximum-scale")) parts.push("maximum-scale=1");
      viewport.content = parts.join(", ");
    }
    const added: HTMLElement[] = [];
    const add = (tag: string, attrs: Record<string, string>) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      document.head.appendChild(el);
      added.push(el);
    };
    add("link", { rel: "manifest", href: "/manifest.webmanifest" });
    add("link", { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png" });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-title", content: title });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "default" });
    return () => {
      for (const el of added) el.remove();
      if (viewport && before !== null) viewport.content = before;
    };
  }, [title]);
}
